import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import BrandLogo from "@/components/layout/BrandLogo";

/**
 * Public / semi-public legal document viewer.
 * Renders the currently-active version of a legal slot (privacy_policy,
 * terms_and_conditions, copyright_notice).
 */
export default function LegalPage({ slot: fixedSlot }) {
  const params = useParams();
  const slot = fixedSlot || params.slot;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/disclaimers/${slot}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not available"));
  }, [slot]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Slim top nav */}
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-back-link">
            <BrandLogo className="h-14 w-auto object-contain" />
            <div className="hidden sm:block leading-tight pl-2.5 border-l border-border">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Investor Portal</div>
            </div>
          </Link>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground uppercase tracking-wider">Back to portal</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {err && <div className="rounded-lg border border-border p-6 text-sm text-muted-foreground">{err}</div>}
        {!err && !d && <div className="text-sm text-muted-foreground">Loading…</div>}
        {d && (
          <article>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Legal</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">{d.title}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground mb-8 border-b border-border pb-6">
              <div className="inline-flex items-center gap-1.5" data-testid="legal-last-updated">
                <span className="uppercase tracking-[0.15em] font-bold text-foreground/70">Last updated</span>
                <span className="num-display">{formatDate(d.last_modified_at || d.effective_at)}</span>
              </div>
              <span className="h-1 w-1 rounded-full bg-border" />
              <div className="inline-flex items-center gap-1.5">
                <span className="uppercase tracking-[0.15em] font-bold text-foreground/70">Effective</span>
                <span className="num-display">{formatDate(d.effective_at)}</span>
              </div>
              <span className="h-1 w-1 rounded-full bg-border" />
              <div className="inline-flex items-center gap-1.5">
                <span className="uppercase tracking-[0.15em] font-bold text-foreground/70">Version</span>
                <span className="num-display">v{d.version}</span>
              </div>
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              {(d.body || "").split("\n\n").map((para, i) => {
                if (para.trim().startsWith("## ")) {
                  return <h2 key={i} className="font-display text-xl font-bold mt-8 mb-2">{para.replace(/^##\s+/, "")}</h2>;
                }
                return <p key={i} className="text-sm leading-relaxed whitespace-pre-line mb-4">{para}</p>;
              })}
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
